import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Package,
  Truck,
  Users,
  Trophy,
  BarChart,
  MapPin,
  Edit,
  Save,
  X,
  CheckCircle,
  XCircle,
  Clock,
  User
} from 'lucide-react'

export function ManagementPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    activeDeliveryPartners: 0,
    pendingDeliveryPartners: 0,
    activeDeliveries: 0,
    completedDeliveries: 0,
    successRate: 0
  })
  const [deliveryPartners, setDeliveryPartners] = useState<any[]>([])
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [editingRegion, setEditingRegion] = useState(false)
  const [region, setRegion] = useState(user?.region || '')

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    if (user.role !== 'manager') {
      navigate('/')
      return
    }

    fetchStats()
    fetchDeliveryPartners()
  }, [user, navigate])

  useEffect(() => {
    // Update region state when user data changes
    if (user) {
      setRegion(user.region || '')
    }
  }, [user])

  const fetchStats = async () => {
    if (!user) return

    try {
      // Get active delivery partners count
      const { count: activeDeliveryPartners, error: activeError } = await supabase
        .from('delivery_partners')
        .select('*', { count: 'exact' })
        .eq('manager_id', user.id)
        .eq('status', 'active')

      if (activeError) throw activeError

      // Get pending delivery partners count
      const { count: pendingDeliveryPartners, error: pendingError } = await supabase
        .from('delivery_partners')
        .select('*', { count: 'exact' })
        .eq('manager_id', user.id)
        .eq('status', 'pending')

      if (pendingError) throw pendingError

      // Get active deliveries count
      const { count: activeDeliveries, error: activeDelError } = await supabase
        .from('delivery_requests')
        .select('*', { count: 'exact' })
        .in('current_step', ['accepted', 'preparing', 'pickup_ready', 'picked_up', 'in_transit', 'arrived'])
        .eq('manager_id', user.id)

      if (activeDelError) throw activeDelError

      // Get completed deliveries
      const { data: allDeliveries, error: allDelError } = await supabase
        .from('delivery_requests')
        .select('current_step')
        .eq('manager_id', user.id)

      if (allDelError) throw allDelError

      const completedDeliveries = allDeliveries?.filter(d => d.current_step === 'delivered').length || 0
      const totalDeliveries = allDeliveries?.length || 0
      const successRate = totalDeliveries > 0 ? (completedDeliveries / totalDeliveries) * 100 : 0

      setStats({
        activeDeliveryPartners: activeDeliveryPartners || 0,
        pendingDeliveryPartners: pendingDeliveryPartners || 0,
        activeDeliveries: activeDeliveries || 0,
        completedDeliveries,
        successRate
      })
    } catch (err) {
      console.error('Error fetching stats:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchDeliveryPartners = async () => {
    if (!user) return

    try {
      const { data, error } = await supabase
        .from('delivery_partners')
        .select(`
          id,
          applicant_name,
          applicant_email,
          vehicle_type,
          delivery_zone,
          status,
          rating,
          total_deliveries,
          created_at
        `)
        .eq('manager_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setDeliveryPartners(data || [])
    } catch (err) {
      console.error('Error fetching delivery partners:', err)
    }
  }

  const handleSaveRegion = async () => {
    if (!user) return

    try {
      setLoading(true)
      
      // Update the user's region in the database
      const { error } = await supabase
        .from('profiles')
        .update({
          region: region
        })
        .eq('id', user.id)

      if (error) throw error

      // Update local user state
      useAuthStore.getState().loadUser()

      setToastMessage('Région mise à jour avec succès')
      setToastType('success')
      setShowToast(true)
      setEditingRegion(false)
    } catch (err) {
      console.error('Error updating region:', err)
      setToastMessage('Erreur lors de la mise à jour de la région')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Actif
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            En attente
          </span>
        )
      case 'blocked':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Bloqué
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Tableau de Bord Manager
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Gérez votre équipe de livraison et suivez les performances
          </p>
        </div>

        {/* Manager Info Card */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <div className="flex items-center">
                  <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <h2 className="text-xl font-bold">{user?.full_name || 'Manager'}</h2>
                    <p className="text-white/80">{user?.email}</p>
                  </div>
                </div>
              </div>
              <div>
                {editingRegion ? (
                  <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <div className="relative w-full sm:w-auto">
                      <MapPin className="h-5 w-5 text-white/70 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        className="pl-10 pr-3 py-2 w-full bg-white/10 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50"
                        placeholder="Entrez votre région"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-white border-white/30 hover:bg-white/10"
                        onClick={handleSaveRegion}
                        disabled={loading}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Enregistrer
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-white border-white/30 hover:bg-white/10"
                        onClick={() => {
                          setRegion(user?.region || '')
                          setEditingRegion(false)
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center bg-white/10 px-4 py-2 rounded-lg">
                      <MapPin className="h-5 w-5 text-white/70 mr-2" />
                      <span>{user?.region || 'Région non définie'}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-white border-white/30 hover:bg-white/10"
                      onClick={() => setEditingRegion(true)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Livreurs Actifs
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {stats.activeDeliveryPartners}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Livreurs en Attente
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {stats.pendingDeliveryPartners}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Truck className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Livraisons Actives
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {stats.activeDeliveries}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Livraisons Terminées
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {stats.completedDeliveries}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BarChart className="h-6 w-6 text-indigo-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Taux de Réussite
                    </dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {stats.successRate.toFixed(1)}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
          <Link
            to="/management/competitions"
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Trophy className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Compétitions</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Gérez les compétitions entre équipes de livraison
                  </p>
                </div>
              </div>
            </div>
          </Link>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/management/deliveries')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Truck className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Livraisons</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Suivez les livraisons en cours
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/management/team')}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <h3 className="text-lg font-medium text-gray-900">Équipe</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Gérez votre équipe de livreurs
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Partners List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Livreurs Assignés
            </h3>
          </div>
          <div className="p-4">
            {deliveryPartners.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun livreur</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Vous n'avez pas encore de livreurs assignés
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Livreur
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Véhicule
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Zone
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Livraisons
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Note
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deliveryPartners.map((partner) => (
                      <tr key={partner.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate('/management/team')}>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0">
                              <div className="h-full w-full rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                              </div>
                            </div>
                            <div className="ml-3 sm:ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {partner.applicant_name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                                {partner.applicant_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                          <div className="text-sm text-gray-900 capitalize">
                            {partner.vehicle_type === 'bike' ? 'Vélo' :
                             partner.vehicle_type === 'motorcycle' ? 'Moto' :
                             partner.vehicle_type === 'car' ? 'Voiture' :
                             partner.vehicle_type === 'van' ? 'Camionnette' : 'À pied'}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                          <div className="text-sm text-gray-900">
                            {partner.delivery_zone}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                          <div className="text-sm text-gray-900">
                            {partner.total_deliveries || 0}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                          <div className="text-sm text-gray-900">
                            {partner.rating?.toFixed(1) || '0.0'} / 5.0
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(partner.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}